import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Battery, BellRing, Check, CheckCircle2, ChevronsLeft, ChevronsRight, Clock, Copy, FileText, Lock, Moon, PhoneIncoming, Signal, Siren, Sparkles, Sun, Wifi } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
type ConsoleTone = 'vexu' | 'red' | 'blue' | 'amber' | 'neutral';
interface MobileFrameProps {
  children: React.ReactNode;
  className?: string;
  onSimulateCall?: () => void;
  onSimulateEmergencyCall?: () => void;
  onSimulateFree?: () => void;
  onSimulateTrial?: () => void;
  onSimulateConsentWithTrial?: () => void;
  onSimulateConsentWithoutTrial?: () => void;
  onSimulateActive?: () => void;
  onSimulateOverage?: () => void;
  onToggleVexuNotification?: () => void;
  vexuNotificationActive?: boolean;
  /** Human-readable state of the sticky Vexu notification, e.g. "Active". */
  vexuNotificationStatus?: string | null;
}
const PHONE_WIDTH = 390;
const PHONE_HEIGHT = 844;
const TEST_AGENT_NUMBER = '+1 (765) 632-2092';
function toneClasses(tone: ConsoleTone, isLight: boolean) {
  switch (tone) {
    case 'vexu':
      return isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu';
    case 'red':
      return isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/15 text-red-400';
    case 'blue':
      return isLight ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/15 text-blue-400';
    case 'amber':
      return isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/15 text-amber-400';
    default:
      return isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/[0.07] text-zinc-400';
  }
}
function SectionLabel({
  label,
  isLight
}: {
  label: string;
  isLight: boolean;
}) {
  return <span className={`block text-[8.5px] font-bold uppercase tracking-[0.14em] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-535" data-mp="1ntvb85bgwfb9">
      {label}
    </span>;
}
interface ConsoleRowProps {
  isLight: boolean;
  icon: React.ElementType;
  tone: ConsoleTone;
  title: string;
  detail: string;
  badge?: string;
  badgeActive?: boolean;
  onClick: () => void;
}
function ConsoleRow({
  isLight,
  icon: Icon,
  tone,
  title,
  detail,
  badge,
  badgeActive,
  onClick
}: ConsoleRowProps) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-2 rounded-xl border px-2 py-1.5 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06]'}`} data-id="element-536" data-mp="7flcruqx7nys">
      <span className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg ${toneClasses(tone, isLight)}`} data-id="element-537" data-mp="ceo4yelsxzpc">
        <Icon className="h-3 w-3" data-id="element-538" data-mp="ldjdvyg8dnwc" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-539" data-mp="ldt2pipjhp0g">
        <span className="flex items-center gap-1.5" data-id="element-540" data-mp="2s3pxgj981su5">
          <span className={`truncate text-[11.5px] font-semibold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-541" data-mp="2s49dtvj84962">
            {title}
          </span>
          {badge && <span className={`flex-shrink-0 rounded px-1 py-[1px] text-[8px] font-bold uppercase tracking-wider ${badgeActive ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/[0.07] text-zinc-500'}`} data-id="element-542" data-mp="12ndta06qptec">
              {badge}
            </span>}
        </span>
        <span className={`mt-0.5 block truncate text-[9.5px] leading-tight ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-543" data-mp="jvup3q0zc9aq">
          {detail}
        </span>
      </span>
    </button>;
}
interface ConsoleChipProps {
  isLight: boolean;
  icon: React.ElementType;
  tone: ConsoleTone;
  label: string;
  onClick: () => void;
}
function ConsoleChip({
  isLight,
  icon: Icon,
  tone,
  label,
  onClick
}: ConsoleChipProps) {
  return <button type="button" onClick={onClick} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06]'}`} data-id="element-544" data-mp="24a4w6gzcsvef">
      <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-md ${toneClasses(tone, isLight)}`} data-id="element-545" data-mp="553bw5dmul6o">
        <Icon className="h-2.5 w-2.5" data-id="element-546" data-mp="3reo3hx1cfolk" />
      </span>
      <span className={`truncate text-[10.5px] font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-547" data-mp="3re610io38e57">
        {label}
      </span>
    </button>;
}
export function MobileFrame({
  children,
  className = '',
  onSimulateCall,
  onSimulateEmergencyCall,
  onSimulateFree,
  onSimulateTrial,
  onSimulateConsentWithTrial,
  onSimulateConsentWithoutTrial,
  onSimulateActive,
  onSimulateOverage,
  onToggleVexuNotification,
  vexuNotificationActive,
  vexuNotificationStatus
}: MobileFrameProps) {
  const {
    isLight,
    toggleTheme
  } = useTheme();
  const [scale, setScale] = useState(1);
  const [consoleOpen, setConsoleOpen] = useState(true);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const calculateScale = useCallback(() => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Reserve padding around the phone (48px top/bottom, console width right)
    const availableHeight = vh - 96;
    const availableWidth = vw - 300;
    const scaleByHeight = availableHeight / PHONE_HEIGHT;
    const scaleByWidth = availableWidth / PHONE_WIDTH;
    // Use the smaller scale, capped at 1 (never larger than native)
    const newScale = Math.min(scaleByHeight, scaleByWidth, 1);
    // Minimum scale to prevent it from getting too tiny
    setScale(Math.max(newScale, 0.45));
  }, []);
  useEffect(() => {
    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [calculateScale]);
  const copyTestNumber = () => {
    navigator.clipboard?.writeText(TEST_AGENT_NUMBER).catch(() => undefined);
    setCopiedNumber(true);
    window.setTimeout(() => setCopiedNumber(false), 1600);
  };
  return <div style={{
    height: '100dvh'
  }} className={`w-full flex items-center justify-center font-sans transition-colors duration-500 ${isLight ? 'bg-stone-100 text-slate-900' : 'bg-black text-white'}`} data-id="element-548" data-mp="1f44u4y0ia8hb">
      {/* Demo Console — preview any state of the app instantly */}
      <div style={{
      position: 'absolute',
      right: 'clamp(12px, 3vw, 32px)',
      top: '50%',
      transform: 'translateY(-50%)'
    }} className="z-50" data-id="element-549" data-mp="114dmdf0l1obr">
        {consoleOpen ? <div className={`flex max-h-[92dvh] w-[232px] flex-col overflow-hidden rounded-2xl shadow-xl ${isLight ? 'bg-white ring-1 ring-slate-200 shadow-slate-900/[0.06]' : 'bg-zinc-900 ring-1 ring-white/10 shadow-black/40'}`} data-id="element-550" data-mp="1b3kw7s83z0u4">
            {/* Header */}
            <div className="flex-shrink-0 px-3.5 pb-3 pt-3.5" data-id="element-551" data-mp="2hj53uww6xb6a">
              <div className="flex items-center justify-between gap-2" data-id="element-552" data-mp="2hkq0qt5z967b">
                <div className="flex min-w-0 items-center gap-1.5" data-id="element-553" data-mp="2p0jqr7ds19p5">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-vexu" data-id="element-554" data-mp="2u1hiaakofg3b" />
                  <span className={`truncate text-[12.5px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-555" data-mp="2g1pzzxn4ndji">
                    Demo Console
                  </span>
                </div>
                <button onClick={() => setConsoleOpen(false)} aria-label="Collapse demo console" className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600' : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-300'}`} data-id="element-556" data-mp="37txbykrou1rk">
                  <ChevronsRight className="h-3.5 w-3.5" data-id="element-557" data-mp="3qqfe7w45xk22" />
                </button>
              </div>
              <p className={`mt-1 text-[10px] leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-558" data-mp="3vj8hsgnwamck">
                Preview any state instantly — nothing here affects a real
                account.
              </p>
            </div>

            <div className={`flex-1 overflow-y-auto border-t px-3 py-3 ${isLight ? 'border-slate-100' : 'border-white/[0.06]'}`} data-id="element-559" data-mp="2x17x4b4mxfj5">
              {/* Live events */}
              <SectionLabel isLight={isLight} label="Live events" data-id="element-560" data-mp="3pq543i3ebpwl" />
              <div className="mt-1.5 space-y-1.5" data-id="element-561" data-mp="3poc60r6ume07">
                {onSimulateCall && <ConsoleRow isLight={isLight} icon={PhoneIncoming} tone="vexu" title="Incoming call" detail="Rings as if someone dialled you" onClick={onSimulateCall} data-id="element-562" data-mp="3ks3j2rmetgih" />}
                {onSimulateEmergencyCall && <ConsoleRow isLight={isLight} icon={Siren} tone="red" title="Emergency call" detail="Alert → live transcript → flagged log" onClick={onSimulateEmergencyCall} data-id="element-563" data-mp="1zcxwo0khyzu5" />}
                {onToggleVexuNotification && <ConsoleRow isLight={isLight} icon={BellRing} tone={vexuNotificationActive ? 'vexu' : 'neutral'} title="Notification bar" detail="Tap to cycle Active → Busy → DND → Off" badge={vexuNotificationStatus ?? 'Off'} badgeActive={Boolean(vexuNotificationActive)} onClick={onToggleVexuNotification} data-id="element-564" data-mp="tl4a9e0ozbj0" />}
              </div>

              {/* Account state */}
              {(onSimulateFree || onSimulateTrial || onSimulateActive || onSimulateOverage) && <>
                  <div className="mt-4" data-id="element-565" data-mp="3oxe0n1leoew3">
                    <SectionLabel isLight={isLight} label="Account state" data-id="element-566" data-mp="3tuwf17i6zmbh" />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-567" data-mp="2omkgo7lmp5o">
                    {onSimulateFree && <ConsoleChip isLight={isLight} icon={Lock} tone="neutral" label="Free plan" onClick={onSimulateFree} data-id="element-568" data-mp="6pya2tqh3adv" />}
                    {onSimulateTrial && <ConsoleChip isLight={isLight} icon={Clock} tone="blue" label="Free trial" onClick={onSimulateTrial} data-id="element-569" data-mp="lgfdujqkycdx" />}
                    {onSimulateActive && <ConsoleChip isLight={isLight} icon={CheckCircle2} tone="vexu" label="Paid & active" onClick={onSimulateActive} data-id="element-570" data-mp="zcsx9yeocnv1" />}
                    {onSimulateOverage && <ConsoleChip isLight={isLight} icon={AlertTriangle} tone="amber" label="Over limit" onClick={onSimulateOverage} data-id="element-571" data-mp="25xwy1xjcyh81" />}
                  </div>
                </>}

              {/* Consent */}
              {(onSimulateConsentWithTrial || onSimulateConsentWithoutTrial) && <>
                  <div className="mt-4" data-id="element-572" data-mp="3mcgdkikdsj65">
                    <SectionLabel isLight={isLight} label="Consent screen" data-id="element-573" data-mp="3mdho8lxhj3nc" />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-574" data-mp="3hilm2syjazwi">
                    {onSimulateConsentWithTrial && <ConsoleChip isLight={isLight} icon={Sparkles} tone="blue" label="With trial" onClick={onSimulateConsentWithTrial} data-id="element-575" data-mp="9am7l2nrxeqq" />}
                    {onSimulateConsentWithoutTrial && <ConsoleChip isLight={isLight} icon={FileText} tone="neutral" label="No trial" onClick={onSimulateConsentWithoutTrial} data-id="element-576" data-mp="3hp4c4ueiac5l" />}
                  </div>
                </>}

              {/* Appearance */}
              <div className="mt-4" data-id="element-577" data-mp="16yw26wl8d07t">
                <SectionLabel isLight={isLight} label="Appearance" data-id="element-578" data-mp="1azzfpcft2k5v" />
              </div>
              <div className={`mt-1.5 grid grid-cols-2 gap-1 rounded-xl p-1 ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-579" data-mp="1axn1nad64ty3">
                <button onClick={() => !isLight && toggleTheme()} className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${isLight ? 'bg-white text-slate-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-580" data-mp="2rhq242kn38ly">
                  <Sun className="h-3 w-3" data-id="element-581" data-mp="3f83yg5a9btmc" />
                  Light
                </button>
                <button onClick={() => isLight && toggleTheme()} className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${!isLight ? 'bg-zinc-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-582" data-mp="3a6n0ahj5ql9g">
                  <Moon className="h-3 w-3" data-id="element-583" data-mp="1otl9zm8wjzau" />
                  Dark
                </button>
              </div>
            </div>

            {/* Test agent footer */}
            <div className={`flex-shrink-0 border-t px-3.5 py-3 ${isLight ? 'border-slate-100 bg-slate-50/60' : 'border-white/[0.06] bg-white/[0.02]'}`} data-id="element-584" data-mp="1r7yt5iambqo8">
              <div className="flex items-center justify-between gap-2" data-id="element-585" data-mp="20a81q2mbt9yz">
                <div className="min-w-0" data-id="element-586" data-mp="258ja08wz3cf3">
                  <div className="flex items-center gap-1.5" data-id="element-587" data-mp="25788yguwwiro">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-vexu" data-id="element-588" data-mp="17l0vsikjli3p" />
                    <span className={`text-[8.5px] font-bold uppercase tracking-[0.12em] ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-589" data-mp="1cisy86itcmm5">
                      Live test agent
                    </span>
                  </div>
                  <span className={`mt-0.5 block font-mono text-[12px] font-bold leading-none ${isLight ? 'text-slate-800' : 'text-white'}`} data-id="element-590" data-mp="1maynh8hokpxn">
                    {TEST_AGENT_NUMBER}
                  </span>
                </div>
                <button onClick={copyTestNumber} aria-label="Copy test agent number" className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 ring-1 ring-slate-200 hover:bg-white hover:text-slate-700' : 'text-zinc-500 ring-1 ring-white/10 hover:bg-white/5 hover:text-zinc-200'}`} data-id="element-591" data-mp="m80ibwh2qrqb">
                  {copiedNumber ? <Check className="h-3.5 w-3.5 text-vexu" data-id="element-592" data-mp="3up52mb4upayh" /> : <Copy className="h-3.5 w-3.5" data-id="element-593" data-mp="3unahowtkxiue" />}
                </button>
              </div>
              <p className={`mt-1.5 text-[9.5px] leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-594" data-mp="3kr7hhwiafua3">
                Call it from any phone to hear the real Vexu agent answer.
              </p>
            </div>
          </div> : <button onClick={() => setConsoleOpen(true)} aria-label="Open demo console" className={`flex items-center gap-1.5 rounded-full px-3 py-2 shadow-lg transition-all ${isLight ? 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50' : 'bg-zinc-900 text-zinc-300 ring-1 ring-white/10 hover:bg-zinc-800'}`} data-id="element-595" data-mp="3bsn7djqsmsxh">
            <ChevronsLeft className="h-3.5 w-3.5" data-id="element-596" data-mp="2x43uconlnpx1" />
            <span className="text-[11px] font-semibold" data-id="element-597" data-mp="3ksmtoeoo8scz">Demo</span>
          </button>}
      </div>

      {/* Scaled Phone Container */}
      <div style={{
      width: PHONE_WIDTH,
      height: PHONE_HEIGHT,
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      flexShrink: 0
    }} data-id="element-598" data-mp="3ou8f4of9ueys">
        {/* Phone Frame — outer shell */}
        <div className={`relative w-full h-full shadow-2xl rounded-[40px] border-[8px] transition-colors duration-500 ${isLight ? 'bg-[#e8e8e6] border-[#e8e8e6] ring-1 ring-black/[0.04] shadow-black/[0.08]' : 'bg-zinc-900 border-zinc-900 ring-1 ring-white/10'}`} data-id="element-599" data-mp="7m37eli05c0p">
          {/* Inner Screen Mask — clips all content to the inner rounded area */}
          <div className="absolute inset-0 rounded-[32px] overflow-hidden" data-id="element-600" data-mp="hoqosmv0vf7l">
            {/* Status Bar */}
            <div className={`absolute top-0 left-0 right-0 h-12 px-6 flex items-center justify-between z-50 transition-colors duration-500 ${isLight ? 'text-slate-800 bg-white/60 backdrop-blur-2xl' : 'text-white bg-dark/80 backdrop-blur-2xl border-b border-white/5'}`} data-id="element-601" data-mp="qivbcu34i4hb">
              <div className="text-sm font-semibold ml-2" data-id="element-602" data-mp="crgrkl1e5z97">9:41</div>
              <div className="flex items-center gap-1.5" data-id="element-603" data-mp="csrw4n2nvb1d">
                <Signal className="w-4 h-4" data-id="element-604" data-mp="7s46a4q6508q" />
                <Wifi className="w-4 h-4" data-id="element-605" data-mp="7r2lh8jcozdt" />
                <Battery className="w-5 h-5" data-id="element-606" data-mp="1o642p5pt27r8" />
              </div>
            </div>

            {/* Dynamic Island Placeholder */}
            <div className={`absolute top-2 left-1/2 -translate-x-1/2 w-[120px] h-[35px] rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-black ring-1 ring-black/5' : 'bg-black ring-1 ring-white/5'}`} data-id="element-607" data-mp="1j6r6vvgyhqci" />

            {/* Content Area */}
            <div className={`w-full h-full overflow-y-auto pt-12 pb-safe transition-colors duration-500 ${isLight ? 'app-gradient-bg-light' : 'app-gradient-bg'} ${className}`} data-id="element-608" data-mp="3mnnr52xyx7i4">
              {children}
            </div>

            {/* Home Indicator */}
            <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-slate-900/20' : 'bg-white/20'}`} data-id="element-609" data-mp="57640egkoxy7" />
          </div>
        </div>
      </div>
    </div>;
}